// loginService re-throws `error?.response?.data`, so what arrives here is the
// raw body the API sent, or `undefined` when the request never reached it.
//
// Probed against https://api.dev.umb-360.com/api/login:
//
//   wrong password   -> 401 {code: 'INVALID_CREDENTIALS', ...}
//   unknown username -> 401 {code: 'INVALID_CREDENTIALS', ...}  <- identical
//   empty password   -> 400 {message: ['password should not be empty'], ...}
//
// The two credential failures are indistinguishable on purpose, so the combined
// message is the honest answer for both. USER_NOT_FOUND and INVALID_PASSWORD /
// WRONG_PASSWORD are mapped ahead of the server: the day it starts sending one
// of them, the specific message appears with no further change here.
const MESSAGES = {
  invalidCredentials: {
    en: 'invalid username or password',
    fr: "Nom d'utilisateur ou mot de passe invalide",
  },
  wrongUsername: {
    en: 'wrong username',
    fr: "Nom d'utilisateur incorrect",
  },
  wrongPassword: {
    en: 'wrong password',
    fr: 'Mot de passe incorrect',
  },
  emptyUsername: {
    en: 'Enter the Username',
    fr: "Entrez le nom d'utilisateur",
  },
  emptyPassword: {
    en: 'Enter the Password',
    fr: 'Entrez le mot de passe',
  },
};

const CODES = {
  USER_NOT_FOUND: 'wrongUsername',
  INVALID_PASSWORD: 'wrongPassword',
  WRONG_PASSWORD: 'wrongPassword',
};

// A 400 lists every field it refused, so pick the first one we have wording for
// rather than assuming a single-entry array.
const describeValidationError = (error) => {
  const details = [].concat(error.message ?? []);

  if (details.some((detail) => /password should not be empty/i.test(detail))) {
    return 'emptyPassword';
  }

  if (details.some((detail) => /(email|username) should not be empty/i.test(detail))) {
    return 'emptyUsername';
  }

  return null;
};

const pickMessageKey = (error) => {
  if (!error || typeof error !== 'object') {
    return 'invalidCredentials';
  }

  if (error.statusCode === 400) {
    return describeValidationError(error) ?? 'invalidCredentials';
  }

  return CODES[error.code] ?? 'invalidCredentials';
};

// A bodiless 401 and a dropped connection both arrive here as `undefined`, so
// there is nothing to tell them apart. The credential message is the one that
// fits the overwhelmingly more common case at a login form.
export const describeLoginError = (error, isEnglish) =>
  MESSAGES[pickMessageKey(error)][isEnglish ? 'en' : 'fr'];

export const describeEmptyField = (field, isEnglish) =>
  MESSAGES[field === 'password' ? 'emptyPassword' : 'emptyUsername'][
    isEnglish ? 'en' : 'fr'
  ];
